<script setup lang="ts">
import Chip from '@/components/Chip.vue';
import MoveEmployeeDialog from '@/components/dialogs/MoveEmployeeDialog.vue';
import UpsertEmployeeDialog from '@/components/dialogs/UpsertEmployeeDialog.vue';
import List from '@/components/List.vue';
import { OrganizationRole } from '@/enums/roles';
import { useAuthStore } from '@/stores/auth';
import { useEmployeeStore } from '@/stores/employee';
import { useOrganizationStore } from '@/stores/organization';
import type { Employee, OrganizationEmployee } from '@/types/employee';
import { EmployeeStatus } from '@/types/employee';
import type { Organization } from '@/types/organization';
import { toDisplayDate } from '@/utils/date';
import { getTranslatedRoles } from '@/utils/role';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

const employeeStore = useEmployeeStore();
const authStore = useAuthStore();
const organizationStore = useOrganizationStore();

const router = useRouter();

const organizationIdFromUrl = ref(router.currentRoute.value.params.organizationId?.toString());
const organizationId = computed(() => (organizationIdFromUrl.value ? organizationIdFromUrl.value : authStore.getCurrentOrganization()._id));
const organization = ref<Organization | null>();
const isInitializing = ref(true);

async function fetchOrganizationEmployees(): Promise<void> {
    await fetchCurrentOrganizationInformation();
    if (organization.value) await employeeStore.fetchInOrganization(organization.value._id);
}

const headers = [
    { title: 'Status', value: 'status', sortable: true },
    { title: 'Voorletters', value: 'employee.initials', sortable: true },
    { title: 'Voornaam', value: 'employee.firstName', sortable: true },
    { title: 'Achternaam', value: 'employee.lastName', sortable: true },
    { title: 'Email', value: 'employee.email', sortable: true },
    { title: 'Geboortedatum', value: 'employee.dateOfBirth', sortable: true },
    { title: 'Personeelsnummer', value: 'employee.staffNumber', sortable: true },
    { title: 'Functie', value: 'employee.jobTitle', sortable: true },
    { title: 'Rol', value: 'roles', sortable: true }
];

onMounted(async () => {
    await fetchOrganizationEmployees();
    isInitializing.value = false;
});

watch(
    () => organizationId.value,
    async (newValue, oldValue) => {
        if (newValue !== oldValue && newValue != null) {
            await fetchOrganizationEmployees();
            await fetchCurrentOrganizationInformation();
        }
    }
);

async function fetchCurrentOrganizationInformation(): Promise<void> {
    if (organizationId.value) organization.value = await organizationStore.fetchById(organizationId.value);
}

async function toggleEmployeeStatus(employeeId: string, currentStatus: EmployeeStatus): Promise<void> {
    if (!isAllowedToChangeStatus(employeeId)) return;
    const newStatus = currentStatus === EmployeeStatus.Active ? EmployeeStatus.Inactive : EmployeeStatus.Active;
    await organizationStore.updateEmployeeStatus(organizationId.value, employeeId, newStatus);
}

function isAllowedToChangeStatus(employeeId: string) {
    if (authStore.isLoggedInEmployee(employeeId)) return false;
    if (authStore.isSuperAdmin()) return true;
    if (authStore.isReadOnly()) return false;
    if (authStore.isAuthorized([OrganizationRole.Admin])) return true;
    return false;
}

function getStatusText(status: EmployeeStatus) {
    return status === EmployeeStatus.Active ? 'Actief' : 'Inactief';
}

const clickedEmployee = ref<OrganizationEmployee>();
const isUpsertEmployeeDialogOpen = ref(false);

async function openProfile(organizationEmployee: OrganizationEmployee) {
    await router.push(`/medewerker/${(organizationEmployee.employee as Employee)._id}`);
}

async function openEditEmployee(organizationEmployee: OrganizationEmployee) {
    // Employee is always an Employee type, since the items are populated from the server
    if ((organizationEmployee.employee as Employee)._id !== authStore.getLoggedInEmployee()._id) {
        clickedEmployee.value = organizationEmployee;
        isUpsertEmployeeDialogOpen.value = true;
    } else {
        await router.push(`/medewerker/${authStore.getLoggedInEmployee()._id}`);
    }
}

function openAddNewEmployee(): void {
    clickedEmployee.value = undefined;
    isUpsertEmployeeDialogOpen.value = true;
}

async function handleStatusChanged(item: OrganizationEmployee) {
    const employeeId = (item.employee as Employee)._id;
    if (!isAllowedToChangeStatus(employeeId)) return;

    await toggleEmployeeStatus(employeeId, item.status);
}
</script>

<template>
    <v-fade-transition>
        <div v-if="organization">
            <List
                v-if="organization"
                :title="`Medewerkers van ${organization.name}`"
                :headers="headers"
                :items="employeeStore.employeesInOrganizations"
                :sortBy="[{ key: 'employee.lastName' }]"
                :createButtonCallback="() => openAddNewEmployee()"
                entityName="Medewerker"
                :isEditable="true"
                @clickTableRow="(item) => openProfile(item)"
                @clickTableRowEdit="(item) => openEditEmployee(item)"
                :isInitializing="isInitializing"
            >
                <template v-slot:item.roles="{ item }">
                    {{ getTranslatedRoles(item) }}
                </template>

                <template v-slot:item.status="{ item }">
                    <v-tooltip location="bottom" text="Status wijzigen" v-slot:activator="{ props }">
                        <div
                            class="pa-0 d-flex"
                            @click.stop="handleStatusChanged(item)"
                            v-bind="props"
                            :style="{ cursor: !isAllowedToChangeStatus(item.employee._id) ? 'not-allowed' : 'pointer' }"
                        >
                            <Chip
                                v-bind="props"
                                :color="item.status === EmployeeStatus.Active ? 'green' : 'red'"
                                :text="getStatusText(item.status)"
                                :disabled="!isAllowedToChangeStatus(item.employee._id)"
                            />
                        </div>
                    </v-tooltip>
                </template>

                <template v-slot:item.employee.dateOfBirth="{ item }">
                    <span> {{ toDisplayDate(item.employee.dateOfBirth) }}</span>
                </template>
                <template v-slot:item.employee.staffNumber="{ item }">
                    <span> {{ item.employee.staffNumber ?? '-' }}</span>
                </template>
                <template v-slot:item.employee.jobTitle="{ item }">
                    <span> {{ item.employee.jobTitle ?? '-' }}</span>
                </template>
            </List>

            <div v-else>
                <p>Geen bedrijf geselecteerd</p>
            </div>

            <UpsertEmployeeDialog
                v-model="isUpsertEmployeeDialogOpen"
                :organization="organization"
                :employee="clickedEmployee"
                :is-insert="!clickedEmployee"
            />

            <MoveEmployeeDialog :oldOrg="organization" :old-employee="clickedEmployee" />
        </div>
    </v-fade-transition>
</template>
